import { useState, useEffect, useCallback } from "react";
import { Camera, CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { API } from "@/lib/api";

const isImage = (ct) => (ct || "").startsWith("image/");

export default function PhotoGallery({ job }) {
  const photos = [];
  (job.notes_log || []).forEach((n) => {
    if (n.photo) photos.push({ key: `n-${n.id}`, url: `${API}/jobs/${job.id}/notes/${n.id}/photo`, label: n.text || n.by_name });
  });
  (job.documents || []).forEach((d) => {
    if (isImage(d.content_type)) photos.push({ key: `d-${d.id}`, url: `${API}/jobs/${job.id}/documents/${d.id}/download`, label: d.filename });
  });

  const [idx, setIdx] = useState(-1);
  const open = idx >= 0;
  const close = useCallback(() => setIdx(-1), []);
  const prev = useCallback(() => setIdx((i) => (i <= 0 ? photos.length - 1 : i - 1)), [photos.length]);
  const next = useCallback(() => setIdx((i) => (i >= photos.length - 1 ? 0 : i + 1)), [photos.length]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, close, prev, next]);

  if (photos.length === 0) return null;

  let touchX = 0;
  const onTouchStart = (e) => { touchX = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchX;
    if (dx > 50) prev(); else if (dx < -50) next();
  };

  return (
    <div className="mb-4" data-testid="photo-gallery">
      <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <Camera size={14} className="text-accent" /> Site Photos ({photos.length})
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
        {photos.map((p, i) => (
          <button key={p.key} type="button" onClick={() => setIdx(i)} data-testid={`gallery-photo-${p.key}`}
            className="block aspect-square rounded-md overflow-hidden border border-border group cursor-zoom-in">
            <img src={p.url} alt={p.label || "site photo"} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
          </button>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center animate-in fade-in duration-150"
          data-testid="photo-lightbox" onClick={close} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <button type="button" className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors" onClick={close} data-testid="lightbox-close"><X size={28} /></button>
          {photos.length > 1 && (
            <button type="button" className="absolute left-3 sm:left-8 text-white/70 hover:text-white transition-colors" onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev"><CaretLeft size={40} weight="bold" /></button>
          )}
          <figure className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={photos[idx].url} alt={photos[idx].label || "site photo"} className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg shadow-2xl" />
            <figcaption className="mt-3 text-white/80 text-sm text-center px-4">
              {photos[idx].label || ""}<span className="text-white/50 ml-2">{idx + 1} / {photos.length}</span>
            </figcaption>
          </figure>
          {photos.length > 1 && (
            <button type="button" className="absolute right-3 sm:right-8 text-white/70 hover:text-white transition-colors" onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next"><CaretRight size={40} weight="bold" /></button>
          )}
        </div>
      )}
    </div>
  );
}
